import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './StaticPages.css';

const API = process.env.REACT_APP_API_URL || '/api';

interface ProcessStep { icon: string; title: string; title_bn?: string; description: string; description_bn?: string; }
interface RefundRow { method: string; method_bn?: string; time: string; time_bn?: string; }

interface ReturnsData {
  hero_title: string; hero_title_bn?: string;
  hero_subtitle: string; hero_subtitle_bn?: string;
  policy_title: string; policy_title_bn?: string;
  policy_content: string; policy_content_bn?: string;
  policy_note: string; policy_note_bn?: string;
  eligible_title: string; eligible_title_bn?: string;
  eligible_reasons: string[];
  eligible_reasons_bn?: string[];
  non_eligible_note: string; non_eligible_note_bn?: string;
  process_title: string; process_title_bn?: string;
  process_steps: ProcessStep[];
  refund_title: string; refund_title_bn?: string;
  refund_content: string; refund_content_bn?: string;
  refund_table: RefundRow[];
  exchange_title: string; exchange_title_bn?: string;
  exchange_content: string; exchange_content_bn?: string;
}

const ReturnsExchange: React.FC = () => {
  const { i18n } = useTranslation();
  const isBn = i18n.language === 'bn';
  const [cms, setCms] = useState<ReturnsData | null>(null);

  useEffect(() => {
    fetch(`${API}/pages/returns/`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCms(d))
      .catch(() => {});
  }, []);

  const t = (en: string | undefined, bn: string | undefined, fallback: string) =>
    isBn ? (bn || en || fallback) : (en || fallback);

  const c = cms;
  const hero_title = t(c?.hero_title, c?.hero_title_bn, 'Returns & Exchange');
  const hero_subtitle = t(c?.hero_subtitle, c?.hero_subtitle_bn, 'Not satisfied with your order? We make it easy to return or exchange items.');
  const policy_title = t(c?.policy_title, c?.policy_title_bn, 'Return & Exchange Policy');
  const policy_content = t(c?.policy_content, c?.policy_content_bn, 'Your satisfaction is our priority. If you receive an item that is defective, damaged, or not as described, you may request a return or exchange within 7 days of delivery.');
  const policy_note = t(c?.policy_note, c?.policy_note_bn, 'Items must be unused, in original packaging, and returned in the same condition as received.');
  const eligible_title = t(c?.eligible_title, c?.eligible_title_bn, 'Accepted Return Reasons');
  const defaultReasons = [
    'Item arrived damaged or broken',
    'Wrong item was delivered',
    'Item is significantly different from the product description or photos',
    'Item is defective or not functioning as described',
    'Missing parts or accessories',
  ];
  const eligible_reasons_en = c?.eligible_reasons || defaultReasons;
  const eligible_reasons = isBn
    ? (c?.eligible_reasons_bn && c.eligible_reasons_bn.length > 0 ? c.eligible_reasons_bn : eligible_reasons_en)
    : eligible_reasons_en;
  const non_eligible_note = t(c?.non_eligible_note, c?.non_eligible_note_bn, 'Items that have been used, washed, altered, or damaged after delivery.');
  const process_title = t(c?.process_title, c?.process_title_bn, 'How to Request a Return or Exchange');
  const process_steps = c?.process_steps || [
    { icon: '1\uFE0F\u20E3', title: 'Contact Us', description: 'Reach out within 7 days of delivery with your order number.' },
    { icon: '2\uFE0F\u20E3', title: 'Send Photos', description: 'Provide clear photos of the item and packaging.' },
    { icon: '3\uFE0F\u20E3', title: 'Approval', description: 'Our team will review your request within 1\u20132 business days.' },
    { icon: '4\uFE0F\u20E3', title: 'Ship Back', description: "If approved, we'll arrange a pickup or guide you on sending the item back." },
  ];
  const refund_title = t(c?.refund_title, c?.refund_title_bn, 'Refund Process');
  const refund_content = t(c?.refund_content, c?.refund_content_bn, 'Once we receive and inspect the returned item, we will notify you of the refund decision.');
  const refund_table = c?.refund_table || [
    { method: 'bKash / Nagad', time: '1\u20133 business days after approval' },
    { method: 'Bank Transfer', time: '3\u20137 business days after approval' },
    { method: 'Store Credit', time: 'Within 24 hours of approval' },
  ];
  const exchange_title = t(c?.exchange_title, c?.exchange_title_bn, 'Product Exchange');
  const exchange_content = t(c?.exchange_content, c?.exchange_content_bn, "If you prefer an exchange instead of a refund, we'll ship the replacement item as soon as the original is returned.");

  return (
    <div className="static-page">
      <div className="static-hero">
        <h1>{hero_title}</h1>
        <p>{hero_subtitle}</p>
      </div>

      <div className="static-content">
        {/* Policy */}
        <div className="static-section">
          <span className="section-tag">{isBn ? '\u0986\u09AE\u09BE\u09A6\u09C7\u09B0 \u09A8\u09C0\u09A4\u09BF' : 'Our Policy'}</span>
          <h2>{policy_title}</h2>
          <p>{policy_content}</p>
          {policy_note && (
            <div className="policy-callout"><strong>{isBn ? '\u0997\u09C1\u09B0\u09C1\u09A4\u09CD\u09AC\u09AA\u09C2\u09B0\u09CD\u09A3:' : 'Important:'}</strong> {policy_note}</div>
          )}
        </div>

        <hr className="static-divider" />

        {/* Eligible Reasons */}
        <div className="static-section">
          <span className="section-tag">{isBn ? '\u09AF\u09CB\u0997\u09CD\u09AF\u09A4\u09BE' : 'Eligibility'}</span>
          <h2>{eligible_title}</h2>
          <p>{isBn ? '\u0986\u09AE\u09B0\u09BE \u09A8\u09BF\u09AE\u09CD\u09A8\u09B2\u09BF\u0996\u09BF\u09A4 \u0995\u09BE\u09B0\u09A3\u09C7 \u09B0\u09BF\u099F\u09BE\u09B0\u09CD\u09A8 \u0997\u09CD\u09B0\u09B9\u09A3 \u0995\u09B0\u09BF:' : 'We accept returns and exchanges for the following reasons:'}</p>
          <ul>
            {eligible_reasons.map((reason, i) => <li key={i}>{reason}</li>)}
          </ul>
          {non_eligible_note && (
            <p style={{ marginTop: 14 }}><strong>{isBn ? '\u09B0\u09BF\u099F\u09BE\u09B0\u09CD\u09A8\u09AF\u09CB\u0997\u09CD\u09AF \u09A8\u09AF\u09BC:' : 'Not eligible for return:'}</strong> {non_eligible_note}</p>
          )}
        </div>

        <hr className="static-divider" />

        {/* Process Steps */}
        <div className="static-section">
          <span className="section-tag">{isBn ? '\u09AA\u09CD\u09B0\u0995\u09CD\u09B0\u09BF\u09AF\u09BC\u09BE' : 'Process'}</span>
          <h2>{process_title}</h2>
          <div className="info-grid">
            {process_steps.map((step, i) => (
              <div className="static-info-card" key={i}>
                <div className="static-info-card-icon">{step.icon}</div>
                <h4>{isBn ? (step.title_bn || step.title) : step.title}</h4>
                <p>{isBn ? (step.description_bn || step.description) : step.description}</p>
              </div>
            ))}
          </div>
        </div>

        <hr className="static-divider" />

        {/* Refunds */}
        <div className="static-section">
          <span className="section-tag">{isBn ? '\u09B0\u09BF\u09AB\u09BE\u09A8\u09CD\u09A1' : 'Refunds'}</span>
          <h2>{refund_title}</h2>
          <p>{refund_content}</p>
          {refund_table.length > 0 && (
            <div className="table-wrapper">
              <table className="static-table">
                <thead><tr>
                  <th>{isBn ? '\u09B0\u09BF\u09AB\u09BE\u09A8\u09CD\u09A1 \u09AA\u09A6\u09CD\u09A7\u09A4\u09BF' : 'Refund Method'}</th>
                  <th>{isBn ? '\u09AA\u09CD\u09B0\u0995\u09CD\u09B0\u09BF\u09AF\u09BC\u09BE\u0995\u09B0\u09A3 \u09B8\u09AE\u09AF\u09BC' : 'Processing Time'}</th>
                </tr></thead>
                <tbody>
                  {refund_table.map((row, i) => (
                    <tr key={i}>
                      <td>{isBn ? (row.method_bn || row.method) : row.method}</td>
                      <td>{isBn ? (row.time_bn || row.time) : row.time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <hr className="static-divider" />

        {/* Exchange */}
        <div className="static-section">
          <span className="section-tag">{isBn ? '\u098F\u0995\u09CD\u09B8\u099A\u09C7\u099E\u09CD\u099C' : 'Exchange'}</span>
          <h2>{exchange_title}</h2>
          {exchange_content.split('\n').filter(Boolean).map((p, i) => (
            <p key={i} style={i > 0 ? { marginTop: 12 } : undefined}>{p}</p>
          ))}
        </div>

        <hr className="static-divider" />

        {/* CTA */}
        <div className="static-section" style={{ textAlign: 'center' }}>
          <h2>{isBn ? '\u09B0\u09BF\u099F\u09BE\u09B0\u09CD\u09A8 \u09B6\u09C1\u09B0\u09C1 \u0995\u09B0\u09A4\u09C7 \u099A\u09BE\u09A8?' : 'Ready to Start a Return?'}</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>{isBn ? '\u0986\u09AE\u09BE\u09A6\u09C7\u09B0 \u099F\u09BF\u09AE \u0986\u09AA\u09A8\u09BE\u09B0 \u09B8\u09AE\u09B8\u09CD\u09AF\u09BE \u09A6\u09CD\u09B0\u09C1\u09A4 \u09B8\u09AE\u09BE\u09A7\u09BE\u09A8 \u0995\u09B0\u09A4\u09C7 \u09AA\u09CD\u09B0\u09B8\u09CD\u09A4\u09C1\u09A4\u0964' : 'Our team is ready to help you resolve any issue quickly.'}</p>
          <Link to="/contact" style={{ display: 'inline-block', padding: '12px 30px', background: 'var(--primary)', color: '#fff', borderRadius: 'var(--radius-sm)', fontWeight: 600, textDecoration: 'none' }}>
            {isBn ? '\u09B8\u09BE\u09AA\u09CB\u09B0\u09CD\u099F\u09C7 \u09AF\u09CB\u0997\u09BE\u09AF\u09CB\u0997 \u0995\u09B0\u09C1\u09A8' : 'Contact Support'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReturnsExchange;
